<script setup>
import { computed, ref, watch } from 'vue';
import { parseISO } from 'date-fns';
import { useLocale } from 'shared/composables/useLocale';
import { getWeeksForMonth } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import CalendarDayCell from './CalendarDayCell.vue';

const props = defineProps({
  currentDate: {
    type: Date,
    required: true,
  },
  events: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectEvent', 'moveEvent']);

const { resolvedLocale } = useLocale();

const weeks = computed(() => getWeeksForMonth(props.currentDate));

const weekdayLabels = computed(() => {
  const formatter = new Intl.DateTimeFormat(resolvedLocale.value, {
    weekday: 'long',
  });
  return weeks.value[0].map(day => ({
    key: day.getDay(),
    label: formatter.format(day),
  }));
});

// vuedraggable needs an independent, directly mutable array per day cell
// (not a filtered view of one shared array), so each day's events are
// bucketed here and rebuilt whenever the source events/month change.
const dayBuckets = ref({});

const isSameDay = (eventDate, day) =>
  eventDate.getFullYear() === day.getFullYear() &&
  eventDate.getMonth() === day.getMonth() &&
  eventDate.getDate() === day.getDate();

const buildBuckets = () => {
  const buckets = {};
  weeks.value.forEach(week => {
    week.forEach(day => {
      const key = day.getTime();
      buckets[key] = props.events
        .filter(event => isSameDay(parseISO(event.startsAt), day))
        .map(event => ({ ...event, calendarSourceDayKey: key }));
    });
  });
  dayBuckets.value = buckets;
};

watch(() => [props.events, props.currentDate], buildBuckets, {
  immediate: true,
});

const handleChange = (day, changeEvent) => {
  if (!changeEvent.added) return;

  const { element } = changeEvent.added;
  emit('moveEvent', {
    event: element,
    fromDay: new Date(element.calendarSourceDayKey),
    toDay: day,
  });
};
</script>

<template>
  <div class="flex flex-col">
    <div class="grid grid-cols-7">
      <div
        v-for="weekday in weekdayLabels"
        :key="weekday.key"
        class="py-2 text-xs font-medium text-center text-n-slate-11"
      >
        {{ weekday.label }}
      </div>
    </div>
    <div class="grid grid-cols-7 border-t border-l rounded-lg border-n-weak">
      <div
        v-for="week in weeks"
        :key="week[0].getTime()"
        class="grid grid-cols-7 col-span-7"
      >
        <CalendarDayCell
          v-for="day in week"
          :key="day.getTime()"
          :day="day"
          :reference-date="currentDate"
          :events="dayBuckets[day.getTime()] || []"
          :disabled="disabled"
          @select-event="emit('selectEvent', $event)"
          @change="handleChange(day, $event)"
        />
      </div>
    </div>
  </div>
</template>
